import React from 'react';
import { ReactComponent as Download } from 'assets/icons/file-download.svg';
import { ReactComponent as File } from 'assets/icons/file.svg';
import { ChatMessage, ChatMessageLocale } from './interfaces';
import Avatar from 'components/avatarComponent';
import { Roles } from '../../../firebase/interfaces';
import { decodeMediaInChat } from 'utilities/functions';
import { formatFileType } from 'utilities/fileHelpers';
import { ContentType } from 'graphql/generated/hasura';
import { twJoin } from 'tailwind-merge';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import ImageMiniComponent from '../../../components/ImageMiniComponent';

const IMG_FILES_TYPE = [
  ContentType.Jpg,
  ContentType.Jpeg,
  ContentType.Png,
  ContentType.Gif,
  ContentType.Webp,
];

interface ChatMessageProps extends ChatMessage {
  locale: ChatMessageLocale;
  appointmentId?: string;
  setSrc: React.Dispatch<React.SetStateAction<string | null | undefined>>;
}

export const ChatMessageComponent: React.FC<ChatMessageProps> = ({
  locale,
  message,
  messageGroup,
  senderPicture,
  senderType,
  setSrc,
  appointmentId,
}) => {
  const { fileName, contentType, mediaUrl, mediaId } =
    decodeMediaInChat(message as string) ?? {};
  const fileType = formatFileType(contentType as string);
  const { getUrlFileFromVonageStorage } = useGetFileFromStorage();

  const reschedule = async (mediaId?: string) => {
    if (!mediaId || !appointmentId) {
      return;
    }
    return await getUrlFileFromVonageStorage(mediaId, appointmentId);
  };

  const openOrDownload = async (
    id: string,
    appointmentId: string,
    fileName: string,
    ext?: string | null,
    fileUrl?: string,
  ) => {
    try {
      const url = await getUrlFileFromVonageStorage(id, appointmentId);
      if (!url) throw new Error('File not found');

      const safeExt = (ext ?? '').toLowerCase();

      if (safeExt === 'pdf') {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }

      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) throw new Error(`Failed to fetch file: ${res.status}`);

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = safeExt ? `${fileName}.${safeExt}` : fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      if (fileUrl) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className={`flex flex-row items-end gap-3 ${
          senderType === Roles.PROVIDER
            ? 'ml-[85px] justify-end'
            : 'mr-[85px] justify-start'
        }`}
      >
        {fileType ? (
          IMG_FILES_TYPE.includes(fileType as ContentType) ? (
            <ImageMiniComponent
              altText={locale?.mediaAlt}
              initialMediaUrl={mediaUrl}
              reschedule={reschedule}
              mediaId={mediaId}
              setSrc={setSrc}
              className={`bg-frost-blue text-med-gray rounded-[7px] w-[120px] h-20 border object-cover ${
                senderType === Roles.PROVIDER
                  ? 'rounded-br-none order-1'
                  : 'rounded-bl-none order-2'
              }`}
            />
          ) : (
            <div
              className={`text-base gap-2.5 py-2.5 px-[15px] overflow-hidden rounded-[7px] break-words ${
                senderType === Roles.PROVIDER
                  ? 'bg-azure-blue text-white rounded-br-none order-1'
                  : 'bg-frost-blue text-clc-blue rounded-bl-none order-2'
              }`}
            >
              <div
                className="flex cursor-pointer items-center gap-2 w-full underline"
                onClick={() =>
                  openOrDownload(
                    mediaId || '',
                    appointmentId ?? '',
                    fileName || '',
                    fileType,
                    mediaUrl,
                  )
                }
              >
                <File
                  className={twJoin(
                    'w-5 h-5 min-w-[20px] fill-clc-blue',
                    senderType === Roles.PROVIDER && 'fill-white',
                  )}
                />

                <div className="flex items-center flex-1 min-w-0">
                  <span className="truncate">{fileName}</span>
                  <span className="whitespace-nowrap flex-shrink-0 lowercase">
                    .{fileType}
                  </span>
                </div>

                <Download
                  className={twJoin(
                    'w-5 h-5 min-w-[20px] fill-clc-blue',
                    senderType === Roles.PROVIDER && 'fill-white',
                  )}
                />
              </div>
            </div>
          )
        ) : messageGroup && messageGroup?.length > 0 ? (
          <div
            className={`flex flex-col overflow-hidden gap-[2px] ${
              senderType === Roles.PROVIDER ? 'order-1' : 'order-2'
            }`}
          >
            {messageGroup.map((message, index) => (
              <span
                key={message.id}
                className={`text-base py-2.5 px-[15px] rounded-[7px] break-words ${
                  index === 0
                    ? 'rounded-b-none'
                    : index === messageGroup.length - 1
                    ? 'rounded-t-none'
                    : 'rounded-b-none rounded-t-none'
                } ${
                  senderType === Roles.PROVIDER
                    ? 'bg-azure-blue text-white'
                    : 'bg-frost-blue text-med-gray'
                }`}
              >
                {message.message}
              </span>
            ))}
          </div>
        ) : (
          <span
            className={`text-base py-2.5 px-[15px] overflow-hidden rounded-[7px] break-words ${
              senderType === Roles.PROVIDER
                ? 'bg-azure-blue text-white rounded-br-none order-1'
                : 'bg-frost-blue text-med-gray rounded-bl-none order-2'
            }`}
          >
            {message}
          </span>
        )}
        <Avatar imageUrl={senderPicture} size="xs" key={senderPicture} />
      </div>
    </div>
  );
};
